import React, { useMemo } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Scatter
} from "recharts";

const fmt = (n) => `$${Number(n).toFixed(2)}`;

const EventDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload.hasEvent) return null;
  const isTrade = payload.eventType === "trade";
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill={isTrade ? "#28a745" : "#fd7e14"} stroke="#fff" strokeWidth={2} />
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize={10} fill={isTrade ? "#28a745" : "#fd7e14"}>
        {isTrade ? "✓" : "★"}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={ttWrap}>
      <div style={ttDate}>{label}</div>
      <div style={ttRow}><span>Close</span><span style={{ fontWeight: 600 }}>{fmt(d?.close ?? d?.stock_ltp)}</span></div>
      {d?.ohlc && (
        <>
          <div style={ttRow}><span style={{ color: "#888" }}>High</span><span>{fmt(d.ohlc.high)}</span></div>
          <div style={ttRow}><span style={{ color: "#888" }}>Low</span><span>{fmt(d.ohlc.low)}</span></div>
        </>
      )}
      {d?.hasEvent && (
        <div style={{ marginTop: 4, fontSize: 10, color: "#fd7e14" }}>
          {d.events?.map(e => e.action_type).join(", ")}
        </div>
      )}
    </div>
  );
};

export default function StockPriceChart({ frames, currentIndex }) {
  const data = useMemo(() => {
    return frames.slice(0, currentIndex + 1).map((f, i) => {
      const hasEvent = f.events?.length > 0;
      const isTrade  = f.events?.some(e => e.auto_executed);
      return {
        date:      f.date?.slice(5),   // "MM-DD"
        fullDate:  f.date,
        stock_ltp: f.stock_ltp,
        close:     f.ohlc?.close ?? f.stock_ltp,
        high:      f.ohlc?.high,
        low:       f.ohlc?.low,
        ohlc:      f.ohlc,
        hasEvent,
        eventType: isTrade ? "trade" : "suggestion",
        events:    f.events || [],
        dotY:      hasEvent ? (f.ohlc?.close ?? f.stock_ltp) : null,
      };
    });
  }, [frames, currentIndex]);

  if (!data.length) return <div style={empty}>No price data</div>;

  const prices = data.map(d => d.close).filter(Boolean);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const pad  = (maxP - minP) * 0.08 || 2;

  // Strike reference line from current frame
  const currentFrame = frames[currentIndex];
  const callStrike   = currentFrame?.positions?.options?.[0]?.strike;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: "#999" }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          domain={[minP - pad, maxP + pad]}
          tick={{ fontSize: 10, fill: "#999" }}
          tickLine={false}
          tickFormatter={v => `$${v.toFixed(0)}`}
          width={52}
        />
        <Tooltip content={<CustomTooltip />} />

        {callStrike && (
          <ReferenceLine
            y={callStrike}
            stroke="#6f42c1"
            strokeDasharray="5 3"
            label={{ value: `Call $${callStrike}`, position: "insideTopRight", fontSize: 10, fill: "#6f42c1" }}
          />
        )}

        <Line
          type="monotone"
          dataKey="close"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: "#3b82f6" }}
        />

        <Scatter
          dataKey="dotY"
          shape={<EventDot />}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const ttWrap  = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };
const ttDate  = { fontWeight: 700, color: "#2c3e50", marginBottom: 4, fontSize: 11 };
const ttRow   = { display: "flex", justifyContent: "space-between", gap: 16, color: "#555" };
const empty   = { height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 13 };
